/** Pure CSV → screening-batch-row mapping + client-side validation.
 *
 * The upload page parses the CSV (via `parseCsv`), auto-detects columns
 * from the header row, resolves country names/codes to alpha-2 against
 * `JURISDICTIONS` (plus pragmatic aliases like "England" → GB), and
 * pre-validates rows so most problems are fixed before the atomic
 * submit. The backend re-validates authoritatively — this layer exists
 * for fast operator feedback, not enforcement.
 *
 * A row may be a person, a company or a vessel (its own `subject_type`
 * cell, else the batch default). A vessel row must carry an IMO number,
 * which is its duplicate key; a jurisdiction is optional for every row and
 * a cell may hold several codes split on `;` `,` `|` or `/`.
 */

import { JURISDICTIONS } from '@/constants/jurisdictions';
import { isVesselSubjectType } from '@/constants/subject-types';
import { ScreeningBatchRowInput } from '@/types';
import { imoCheckDigitOk, normaliseImo } from '@/utils/imo';

export type ColumnField =
  | 'subject_name'
  | 'jurisdiction'
  | 'reference'
  | 'subject_type'
  | 'date_of_birth'
  | 'imo_number'
  | 'address'
  | 'registration_number'
  | 'additional_info';

const HEADER_ALIASES: Record<ColumnField, string[]> = {
  subject_name: [
    'subject_name',
    'subject name',
    'name',
    'subject',
    'company',
    'companies',
    'entity',
    'vessel',
    'vessel name',
    'vessel_name',
    'ship',
    'ship name',
    'ship_name',
    'case name',
    'case_name',
  ],
  jurisdiction: [
    'jurisdiction',
    'jurisdictions',
    'country',
    'country code',
    'country_code',
    'alpha2',
    'alpha-2',
    'flag',
    'flag state',
    'flag_state',
    'country / flag',
    'country/flag',
  ],
  reference: [
    'reference',
    'ref',
    'customer id',
    'customer_id',
    'client reference',
    'client_reference',
    'id',
  ],
  subject_type: ['subject_type', 'subject type', 'type', 'entity type', 'entity_type'],
  date_of_birth: ['date_of_birth', 'date of birth', 'dob', 'birth date', 'birthdate'],
  imo_number: [
    'imo_number',
    'imo number',
    'imo',
    'imo no',
    'imo no.',
    'imo_no',
    'imo-number',
    'vessel imo',
    'vessel imo number',
    'case imo number',
    'case_imo_number',
  ],
  address: ['address', 'registered address', 'registered_address', 'street address'],
  registration_number: [
    'registration_number',
    'registration number',
    'reg number',
    'reg_number',
    'reg no',
    'company number',
    'company_number',
    'vat',
    'vat number',
    'vat_number',
    'tax id',
    'tax_id',
  ],
  additional_info: ['additional_info', 'additional info', 'info', 'notes', 'details', 'comments'],
};

/** Headers that name a subject type only by convention: a `Type` column in
 * a vessel file holds "Tanker", not "vessel". These map to `subject_type`
 * only when every value in the column is a subject type. */
const AMBIGUOUS_TYPE_HEADERS = new Set(['type', 'entity type', 'entity_type']);

/** Country names that appear in real client files but aren't the ISO
 * names in `JURISDICTIONS`. Keys lowercase. */
const COUNTRY_NAME_ALIASES: Record<string, string> = {
  england: 'GB',
  scotland: 'GB',
  wales: 'GB',
  'northern ireland': 'GB',
  uk: 'GB',
  'great britain': 'GB',
  usa: 'US',
  'united states of america': 'US',
  'reunion island': 'RE',
  reunion: 'RE',
  réunion: 'RE',
  holland: 'NL',
  'czech republic': 'CZ',
  'south korea': 'KR',
  'north korea': 'KP',
  russia: 'RU',
  uae: 'AE',
};

const VALID_SUBJECT_TYPES = new Set([
  'person',
  'company',
  'organisation',
  'organization',
  'vessel',
  'ship',
]);

/** A jurisdiction cell may hold several codes or names. */
const JURISDICTION_SEPARATORS = /[;,|/]+/;

export type ColumnMapping = Partial<Record<ColumnField, number>>;

function columnValues(data: string[][], index: number): string[] {
  return data.map((row) => (row[index] ?? '').trim()).filter(Boolean);
}

/** Map header cells → row fields by alias; unmatched fields are absent.
 * `data` (the rows under the header) decides the ambiguous `type` header:
 * with rows present it maps to the subject type only when every value is
 * one, so a vessel file's "Vessel Type: Tanker" column is left alone. */
export function detectColumns(header: string[], data: string[][] = []): ColumnMapping {
  const mapping: ColumnMapping = {};
  header.forEach((raw, index) => {
    const cell = raw.trim().toLowerCase();
    if (!cell) {
      return;
    }
    for (const field of Object.keys(HEADER_ALIASES) as ColumnField[]) {
      if (mapping[field] === undefined && HEADER_ALIASES[field].includes(cell)) {
        if (
          field === 'subject_type' &&
          AMBIGUOUS_TYPE_HEADERS.has(cell) &&
          data.length > 0 &&
          !columnValues(data, index).every((value) => VALID_SUBJECT_TYPES.has(value.toLowerCase()))
        ) {
          return;
        }
        mapping[field] = index;
        return;
      }
    }
  });
  return mapping;
}

const byAlpha2 = new Map(JURISDICTIONS.map((c) => [c.alpha2code.toLowerCase(), c.alpha2code]));
const byAlpha3 = new Map(JURISDICTIONS.map((c) => [c.alpha3code.toLowerCase(), c.alpha2code]));
const byName = new Map(JURISDICTIONS.map((c) => [c.name.toLowerCase(), c.alpha2code]));

/** Resolve a raw country cell (alpha-2, alpha-3, ISO name, or alias) to
 * an alpha-2 code, or null when unresolvable. */
export function resolveJurisdiction(raw: string): string | null {
  const value = raw.trim().toLowerCase();
  if (!value) {
    return null;
  }
  return (
    byAlpha2.get(value) ??
    byAlpha3.get(value) ??
    byName.get(value) ??
    COUNTRY_NAME_ALIASES[value] ??
    null
  );
}

/** The parts of a jurisdiction cell ("GB; US", "Panama / Liberia"). */
export function splitJurisdictionCell(raw: string): string[] {
  return raw
    .split(JURISDICTION_SEPARATORS)
    .map((part) => part.trim())
    .filter(Boolean);
}

/** Resolve every part of a jurisdiction cell: the alpha-2 codes found (in
 * order, de-duplicated) and the parts that did not resolve. */
export function resolveJurisdictions(raw: string): { codes: string[]; unresolved: string[] } {
  const codes: string[] = [];
  const unresolved: string[] = [];
  for (const part of splitJurisdictionCell(raw)) {
    const code = resolveJurisdiction(part);
    if (code === null) {
      unresolved.push(part);
    } else if (!codes.includes(code)) {
      codes.push(code);
    }
  }
  return { codes, unresolved };
}

/** The operator's fix for a cell: the unresolved parts become `fix`, the
 * resolved parts stay, so "Germany; Atlantis" fixed to GB reads "Germany; GB". */
export function applyCountryFix(rawCell: string, fix: string): string {
  const parts = splitJurisdictionCell(rawCell);
  if (parts.length === 0) {
    return fix;
  }
  const fixed = parts.map((part) => (resolveJurisdiction(part) === null ? fix : part));
  return [...new Set(fixed)].join('; ');
}

/** Canonical template header — the download button serialises exactly
 * these names, and detectColumns maps every one of them back. */
export const TEMPLATE_COLUMNS = [
  'subject_name',
  'jurisdiction',
  'reference',
  'subject_type',
  'date_of_birth',
  'imo_number',
  'address',
  'registration_number',
  'additional_info',
] as const;

export function templateCsv(): string {
  return `${TEMPLATE_COLUMNS.join(',')}\n`;
}

export interface DraftRow {
  /** 1-based data-row index — matches the backend's row_errors numbering. */
  index: number;
  subjectName: string;
  rawJurisdiction: string;
  /** The cell's resolved alpha-2 codes; empty when the cell was empty. */
  jurisdictions: string[];
  /** The cell's parts that did not resolve (operator must fix). */
  unresolvedJurisdictions: string[];
  reference: string;
  /** The row's own subject-type cell, as written. */
  subjectType: string;
  /** What the row screens as: its own cell or the batch default (lower-case). */
  effectiveType: string;
  dateOfBirth: string;
  /** The IMO cell as written. */
  imoNumber: string;
  /** The IMO as seven digits when the cell holds a valid number. */
  imo: string | null;
  address: string;
  registrationNumber: string;
  additionalInfo: string;
  problems: string[];
}

function cellAt(row: string[], index: number | undefined): string {
  return index === undefined ? '' : (row[index] ?? '').trim();
}

/** The problems with a vessel row's IMO cell; the normalised number when it is right. */
function checkImo(imoNumber: string, problems: string[]): string | null {
  if (!imoNumber) {
    problems.push('IMO number is required for a vessel');
    return null;
  }
  const digits = normaliseImo(imoNumber);
  if (digits === null) {
    problems.push(`IMO "${imoNumber}" is not seven digits`);
    return null;
  }
  if (!imoCheckDigitOk(digits)) {
    problems.push(`IMO "${imoNumber}" has a wrong check digit`);
    return null;
  }
  return digits;
}

/** What makes two rows the same subject (plan D4): a vessel is its IMO;
 * anyone else is their name with their codes, or the name alone when no
 * country was given. Null when the row cannot be keyed (its own problems
 * already flag it). */
function duplicateKey(draft: Omit<DraftRow, 'problems'>): { key: string; why: string } | null {
  if (isVesselSubjectType(draft.effectiveType)) {
    return draft.imo ? { key: `imo:${draft.imo}`, why: 'same IMO' } : null;
  }
  if (!draft.subjectName) {
    return null;
  }
  const name = draft.subjectName.toLowerCase();
  if (draft.jurisdictions.length > 0 && draft.unresolvedJurisdictions.length === 0) {
    return {
      key: `name:${name}|${[...draft.jurisdictions].sort().join(',')}`,
      why: 'same name + country',
    };
  }
  if (!draft.rawJurisdiction) {
    return { key: `name:${name}`, why: 'same name, no country' };
  }
  return null;
}

/** Build draft rows from parsed CSV data rows (header already stripped).
 * `defaultType` is the batch's default subject type, applied to rows
 * without one of their own. */
export function buildDraftRows(
  data: string[][],
  mapping: ColumnMapping,
  defaultType = 'company'
): DraftRow[] {
  const seen = new Map<string, number>();
  return data.map((row, i) => {
    const index = i + 1;
    const subjectName = cellAt(row, mapping.subject_name);
    const rawJurisdiction = cellAt(row, mapping.jurisdiction);
    const { codes: jurisdictions, unresolved } = resolveJurisdictions(rawJurisdiction);
    const subjectType = cellAt(row, mapping.subject_type);
    const effectiveType = (subjectType || defaultType).trim().toLowerCase();
    const isVessel = isVesselSubjectType(effectiveType);
    const dateOfBirth = cellAt(row, mapping.date_of_birth);
    const imoNumber = cellAt(row, mapping.imo_number);
    const registrationNumber = cellAt(row, mapping.registration_number);

    const problems: string[] = [];
    if (!subjectName) {
      problems.push('Name is required');
    }
    for (const part of unresolved) {
      problems.push(`Unrecognised country "${part}"`);
    }
    if (subjectType && !VALID_SUBJECT_TYPES.has(subjectType.toLowerCase())) {
      problems.push(`Unknown subject type "${subjectType}"`);
    }
    let imo: string | null = null;
    if (isVessel) {
      imo = checkImo(imoNumber, problems);
      if (dateOfBirth) {
        problems.push('A vessel has no date of birth');
      }
      if (registrationNumber) {
        problems.push('A vessel has no registration number — put the IMO in its own column');
      }
    } else {
      if (imoNumber) {
        problems.push('IMO number applies to vessel rows only');
      }
      if (dateOfBirth && !/^\d{4}-\d{2}-\d{2}$/.test(dateOfBirth)) {
        problems.push(`Date of birth "${dateOfBirth}" is not YYYY-MM-DD`);
      }
    }

    const draft: Omit<DraftRow, 'problems'> = {
      index,
      subjectName,
      rawJurisdiction,
      jurisdictions,
      unresolvedJurisdictions: unresolved,
      reference: cellAt(row, mapping.reference),
      subjectType,
      effectiveType,
      dateOfBirth,
      imoNumber,
      imo,
      address: cellAt(row, mapping.address),
      registrationNumber,
      additionalInfo: cellAt(row, mapping.additional_info),
    };
    const duplicate = duplicateKey(draft);
    if (duplicate) {
      const first = seen.get(duplicate.key);
      if (first === undefined) {
        seen.set(duplicate.key, index);
      } else {
        problems.push(`Duplicate of row ${first} (${duplicate.why})`);
      }
    }

    return { ...draft, problems };
  });
}

/** Serialise clean drafts into the wire payload rows. Empty cells go as
 * null (never ''): the server reads '' as a value to validate. */
export function toPayloadRows(drafts: DraftRow[]): ScreeningBatchRowInput[] {
  return drafts.map((draft) => ({
    subject_name: draft.subjectName,
    jurisdictions: draft.jurisdictions.length > 0 ? draft.jurisdictions : null,
    reference: draft.reference || null,
    subject_type: draft.subjectType || null,
    date_of_birth: draft.dateOfBirth || null,
    imo_number: draft.imo ?? (draft.imoNumber || null),
    address: draft.address || null,
    registration_number: draft.registrationNumber || null,
    additional_info: draft.additionalInfo || null,
  }));
}
